/**
 * Represents a successful operation result as a tuple of [data, null].
 * @template TData - The type of the successful result data.
 */
type Success<TData> = [TData, null]

/**
 * Represents a failed operation result as a tuple of [null, error].
 * @template TError - The type of the error that occurred.
 */
type Failure<TError> = [null, TError]

/**
 * Represents the result of an operation—either a success or a failure.
 * @template TData - The type of the successful result data.
 * @template TError - The type of the error that occurred. Defaults to Error.
 */
type Result<TData, TError = Error> = Failure<TError> | Success<TData>

/**
 * Wraps a Promise and returns a Result type.
 *
 * @example
 * const [data, error] = await tryCatch(fetch('https://api.example.com'))
 *
 * @template TData - The type of the successful result data.
 * @template TError - The type of the error (defaults to unknown).
 * @param promise - The Promise to execute.
 * @returns Promise<Result<TData, TError>> - A Result object containing either data or error.
 */
export async function tryCatch<TData, TError = unknown>(
  promise: Promise<TData>,
): Promise<Result<TData, TError>>

/**
 * Wraps an async function call and returns a Result type.
 *
 * @example
 * const [data, error] = await tryCatch(asyncFunction, ...params)
 *
 * @template TData - The type of the successful result data.
 * @template TParams - The types of parameters the function accepts.
 * @template TError - The type of the error (defaults to unknown).
 * @param fn - An async function to invoke.
 * @param params - Parameters to pass to the function.
 * @returns Promise<Result<TData, TError>> - A Result object containing either data or error.
 */
export async function tryCatch<TData, TParams extends unknown[], TError = unknown>(
  fn: (...param: TParams) => Promise<TData>,
  ...params: TParams
): Promise<Result<TData, TError>>

/**
 * Wraps a synchronous function call and returns a Result type.
 *
 * @example
 * const [data, error] = tryCatch(syncFunction, ...params)
 *
 * @template TData - The type of the successful result data.
 * @template TParams - The types of parameters the function accepts.
 * @template TError - The type of the error (defaults to unknown).
 * @param fn - A synchronous function to invoke.
 * @param params - Parameters to pass to the function.
 * @returns Result<TData, TError> - A Result object containing either data or error.
 */
export function tryCatch<TData, TParams extends unknown[], TError = unknown>(
  fn: (...param: TParams) => TData,
  ...params: TParams
): Result<TData, TError>

/**
 * Executes a Promise, async function, or sync function, and returns a Result type.
 *
 * This utility normalizes error handling by capturing thrown errors and
 * returning them in a tuple for both asynchronous and synchronous usage.
 *
 * Usage examples:
 * ```typescript
 * // With a Promise:
 * const [data, error] = await tryCatch(fetch('https://api.example.com'))
 *
 * // With an async function and parameters:
 * const [data, error] = await tryCatch(fetchWithAuth, url, token)
 *
 * // With a sync function:
 * const [data, error] = tryCatch(JSON.parse, '{"ok": true}')
 * ```
 *
 * @template TData - Type of the successful result data.
 * @template TParams - Function parameter types for callable overloads.
 * @template TError - Type of the error (defaults to unknown).
 * @param promiseOrFn - A Promise or a function to execute.
 * @param params - Parameters for the function, if applicable.
 * @returns A Result tuple:
 *   - If input is a Promise or an async function: Promise<Result<TData, TError>>
 *   - If input is a synchronous function: Result<TData, TError>
 */
export function tryCatch<TData, TParams extends unknown[], TError = unknown>(
  promiseOrFn: Promise<TData> | ((...param: TParams) => Promise<TData> | TData),
  ...params: TParams
): Promise<Result<TData, TError>> | Result<TData, TError> {
  try {
    const dataOrPromise = typeof promiseOrFn === 'function' ? promiseOrFn(...params) : promiseOrFn
    if (dataOrPromise && typeof dataOrPromise === 'object' && 'then' in dataOrPromise) {
      const result = dataOrPromise
        .then((data) => Promise.resolve([data, null] satisfies Result<TData, TError>))
        .catch((error) => Promise.resolve([null, error as TError] satisfies Result<TData, TError>))

      return result
    }
    return [dataOrPromise, null]
  } catch (error) {
    return [null, error as TError]
  }
}
